import React, { useEffect, useMemo, useRef, useState } from "react";
import { ControllerScene } from "../view/ControllerScene";
import TargetCursor from "./TargetCursor";
import CardSwap, { Card } from "./Cards";
import Game from "./Game";
import { Toaster } from "sonner";
import { gsap } from "gsap";
import Lanyard from "../components/Lanyard";
import Lanyard2 from "../components/Lanyard2";
import Lanyard3 from "../components/Lanyard3";
import Modal from "../components/Modal";
import EsportsRegistration from "../components/EsportsRegistration/EsportsRegistration";
import Guidelines from "../components/EsportsRegistration/Guidelines";

// +++ NEW HEADER COMPONENT +++
function Header({ onOpenRegister }: { onOpenRegister: () => void }) {
  return (
    <header className="fixed top-0 left-0 right-0 z-[150] p-4 sm:p-6">
      <div className="container mx-auto flex justify-between items-center bg-black/30 backdrop-blur-md p-3 rounded-xl border border-white/10">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img
            src="/apexlogo.png"
            alt="Apex logo"
            className="w-8 h-8 sm:w-10 sm:h-10"
          />
          <h2
            className="text-lg sm:text-xl font-bold text-white tracking-wider"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            APEX MLRIT
          </h2>
        </div>

        <div className="flex items-center space-x-3">
          {/* View Bracket Button - Hidden for now */}
          {/* <a
                        href="/tournament"
                        className="group relative inline-flex items-center justify-center px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base font-medium text-white bg-gray-700/50 hover:bg-gray-600/70 rounded-lg transition-all duration-300 ease-in-out overflow-hidden"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <span className="relative z-10">View Bracket</span>
                    </a> */}

          {/* Join Tournament Button */}
          <button
            onClick={onOpenRegister}
            className="group relative inline-flex items-center justify-center px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base font-medium text-white bg-gradient-to-r from-crimson-600 to-red-700 rounded-lg shadow-[0_5px_20px_rgba(239,68,68,0.3)] hover:shadow-[0_8px_25px_rgba(239,68,68,0.45)] transition-all duration-300 ease-in-out overflow-hidden cursor-target"
          >
            <span className="absolute left-0 top-0 w-full h-full bg-gradient-to-r from-red-500 to-crimson-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            <svg
              className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h5a3 3 0 013 3v1"
              />
            </svg>
            <span className="relative z-10">Join Tournament</span>
          </button>
        </div>
      </div>
    </header>
  );
}

export function App() {
  const controllerRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const gameSectionRef = useRef<HTMLElement | null>(null);
  const [inGame, setInGame] = useState(false);
  const [footerHovered, setFooterHovered] = useState(false);

  const [isRegisterModalOpen, setRegisterModalOpen] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(true);

  const handleOpenRegister = () => {
    setShowGuidelines(true);
    setRegisterModalOpen(true);
  };

  const handleAcceptGuidelines = () => {
    setShowGuidelines(false);
  };

  const handleCloseModal = () => {
    setRegisterModalOpen(false);
    setShowGuidelines(true);
  };
  // Ensure initial state for controller overlay
  useEffect(() => {
    if (controllerRef.current) {
      gsap.set(controllerRef.current, { opacity: 1, willChange: "opacity" });
    }
  }, []);

  // Handle controller visibility based on scroll position and section visibility
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller) return;

    const handleScroll = () => {
      const lanyardSection = document.getElementById("lanyard");
      if (!lanyardSection) return;

      const rect = lanyardSection.getBoundingClientRect();
      const isLanyardInView =
        rect.top <= window.innerHeight * 0.7 &&
        rect.bottom >= window.innerHeight * 0.3;

      // If lanyard section is in view, fade out controller
      if (isLanyardInView) {
        gsap.to(controller, {
          opacity: 0.05, // Almost fully transparent but still slightly visible
          duration: 0.8,
          ease: "power2.inOut",
          onComplete: () => {
            // Disable pointer events when faded out
            controller.style.pointerEvents = "none";
          },
        });
      } else {
        // Fade back in when not in lanyard section
        gsap.to(controller, {
          opacity: 1,
          duration: 0.8,
          ease: "power2.inOut",
          // Removed pointerEvents = 'auto' here
        });
      }
    };

    // Add scroll event listener to check on scroll
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Also use intersection observer for better performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          handleScroll(); // Always call handleScroll when intersection changes
        });
      },
      {
        root: null,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: "0px 0px -30% 0px", // Trigger when 30% of the element is in view
      }
    );

    const lanyardElement = document.getElementById("lanyard");
    if (lanyardElement) {
      observer.observe(lanyardElement);
    }

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (lanyardElement) {
        observer.unobserve(lanyardElement);
      }
    };
  }, []);

  // Fade controller overlay based on inGame and footer hover
  useEffect(() => {
    const el = controllerRef.current;
    if (!el) return;

    // Always keep pointer-events: none on the controller overlay
    el.style.pointerEvents = "none";

    // Only control opacity for visual effect
    gsap.to(el, {
      opacity: inGame || footerHovered ? 0 : 1,
      duration: 0.6,
      ease: "power2.out",
    });
  }, [inGame, footerHovered]);

  // Lock/unlock scroll when game starts/exits
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    sc.style.overflowY = inGame ? "hidden" : "auto";
    // iOS/Android body lock as well
    document.body.style.overflow = inGame ? "hidden" : "";
    if (gameSectionRef.current) {
      (gameSectionRef.current as HTMLElement).style.touchAction = inGame
        ? "none"
        : "auto";
    }

    // extra guards against scroll: wheel/touch/keys
    const prevent = (e: Event) => {
      e.preventDefault();
    };
    const preventKeys = (e: KeyboardEvent) => {
      const keys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        "Space",
      ];
      if (keys.includes(e.code) || (e.key && keys.includes(e.key))) {
        e.preventDefault();
      }
    };

    const wheelHandler = (e: Event) => {
      e.preventDefault();
    };

    const touchMoveHandler = (e: Event) => {
      e.preventDefault();
    };

    if (inGame) {
      sc.addEventListener("wheel", wheelHandler, { passive: false });
      sc.addEventListener("touchmove", touchMoveHandler, { passive: false });
      window.addEventListener("keydown", preventKeys);
    }

    return () => {
      sc.removeEventListener("wheel", wheelHandler);
      sc.removeEventListener("touchmove", touchMoveHandler);
      window.removeEventListener("keydown", preventKeys);
    };
  }, [inGame]);

  // Observe game section visibility: if scrolled away, exit game mode
  useEffect(() => {
    const target = gameSectionRef.current;
    if (!target) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (inGame && e.intersectionRatio < 0.05) {
          setInGame(false);
        }
      },
      { threshold: [0, 0.05, 0.2, 0.5, 1] }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [inGame]);

  return (
    <div className="w-full h-full relative bg-[#0a0a0b] text-[#e5e7eb]">
      <Toaster
        richColors
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#160506",
            border: "1px solid #ef4444",
            color: "#fecaca",
          },
        }}
      />
      <TargetCursor spinDuration={15} hideDefaultCursor={true} />
      <TemplateWebsite
        scrollRef={scrollRef}
        onGameStart={() => setInGame(true)}
        gameSectionRef={gameSectionRef}
        footerHovered={footerHovered}
        setFooterHovered={setFooterHovered}
        onOpenRegister={handleOpenRegister}
      />
      <div
        ref={controllerRef}
        className="fixed inset-0 pointer-events-none z-[100]"
      >
        <ControllerScene />
      </div>
      {/* Sticky centered-bottom Apex logo is removed as it's now in the header */}
      <Modal isOpen={isRegisterModalOpen} onClose={handleCloseModal}>
        {showGuidelines ? (
          <Guidelines onAccept={handleAcceptGuidelines} onClose={handleCloseModal} />
        ) : (
          <EsportsRegistration onSuccessfulSubmit={handleCloseModal} />
        )}
      </Modal>
    </div>
  );
}

function TemplateWebsite({
  scrollRef,
  onGameStart,
  gameSectionRef,
  footerHovered,
  setFooterHovered,
  onOpenRegister,
}: {
  scrollRef: React.RefObject<HTMLDivElement>;
  onGameStart: () => void;
  gameSectionRef: React.RefObject<HTMLElement>;
  footerHovered: boolean;
  setFooterHovered: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenRegister: () => void;
}) {
  return (
    <div
      id="bg-scroll"
      ref={scrollRef}
      className="h-screen overflow-y-auto overflow-x-hidden bg-gradient-to-b from-[#120709] to-[#0b0b0c]"
    >
      {//<Header onOpenRegister={onOpenRegister} />
      }
      
      <Hero />
      <Features /> {/* Prop removed */}
      {/* Subtle spacer between About (cards) and Showcase */}
      <section aria-hidden="true" className="h-0 sm:h-28" />
      <Showcase />
      {/* Our Team Section */}
      <section
        id="team"
        className="relative py-16 sm:py-24 lg:py-32 overflow-hidden bg-[#160506]/20"
      >
        <div className="container mx-auto px-4">
          <h2
            className="text-3xl text-center sm:text-4xl lg:text-5xl xl:text-6xl mb-6 sm:mb-8 lg:mb-10 tracking-wider uppercase text-white drop-shadow-[0_0_8px_rgba(239,68,68,0.85)]"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            Our Team
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-crimson-500 to-transparent mb-8 sm:mb-12"></div>

          {/* Team image container */}
          <div className="relative max-w-4xl mx-auto group">
            <div className="relative overflow-hidden rounded-2xl border border-crimson-500/30 shadow-[0_16px_40px_rgba(0,0,0,0.35)]">
              <img
                src={new URL("../../assets/team.png", import.meta.url).href}
                alt="Our Team"
                className="w-full h-auto"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src =
                    "https://via.placeholder.com/1600x900/0f172a/ef4444?text=Team+Photo";
                }}
              />
            </div>

            {/* Team description */}
            <div className="mt-6 text-center max-w-2xl mx-auto">
              <p className="text-rose-100/80 text-sm sm:text-base md:text-lg font-light leading-relaxed">
                Meet the passionate gamers and creators behind APEX MLRIT. Our
                team is dedicated to building a thriving gaming community.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Lanyard Section */}
      <section id="lanyard" className="w-full py-12 px-2 sm:px-4 relative z-10">
        <div className="w-full max-w-[1800px] mx-auto">
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 px-4"
            style={{ pointerEvents: "none" }}
          >
            {/* Left: main Lanyard */}
            <div className="w-full h-[400px] sm:h-[500px] lg:h-[600px] relative">
              <div
                style={{ pointerEvents: "auto", width: "100%", height: "100%" }}
              >
                <Lanyard position={[7.5, 0, 15]} gravity={[0, -40, 0]} />
              </div>
            </div>
            {/* Middle: Lanyard2 */}
            <div className="w-full h-[400px] sm:h-[500px] lg:h-[600px] relative">
              <div
                style={{ pointerEvents: "auto", width: "100%", height: "100%" }}
              >
                <Lanyard2 position={[7.75, 0, 15]} gravity={[0, -40, 0]} />
              </div>
            </div>
            {/* Right: Lanyard2 */}
            <div className="w-full h-[400px] sm:h-[500px] lg:h-[600px] relative">
              <div
                style={{ pointerEvents: "auto", width: "100%", height: "100%" }}
              >
                <Lanyard3 position={[7.5, 0, 15]} gravity={[0, -40, 0]} />
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Integrated Game Section */}
      <Game onStart={onGameStart} outerRef={gameSectionRef} />
      <Footer
        onMouseEnter={() => setFooterHovered(true)}
        onMouseLeave={() => setFooterHovered(false)}
      />
    </div>
  );
}

function Hero() {
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const sub1Ref = useRef<HTMLHeadingElement | null>(null);
  const sub2Ref = useRef<HTMLHeadingElement | null>(null);
  const noteRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    const tl = gsap.timeline();
    tl.fromTo(
      titleRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 1.8, ease: "power2.out" }
    )
      .fromTo(
        sub1Ref.current,
        { opacity: 0 },
        { opacity: 1, duration: 1.0, ease: "power2.out" },
        ">-0.3"
      )
      .fromTo(
        sub2Ref.current,
        { opacity: 0 },
        { opacity: 1, duration: 1.0, ease: "power2.out" },
        ">-0.25"
      )
      .fromTo(
        noteRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.9, ease: "power2.out" },
        ">-0.2"
      );
  }, []);

  return (
    <div>
      <section className="h-screen grid place-items-center text-center px-4 sm:px-0 py-8 sm:py-0">
        <div>
          <h1
            ref={titleRef}
            className="opacity-0 cursor-target text-extrabold text-white tracking-wider uppercase -translate-y-[12vh] transform-gpu [will-change:transform] text-6xl drop-shadow-[0_0_8px_rgba(239,68,0,0.85)]"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            APEX MLRIT
          </h1>
          <br />
          <h1
            ref={sub1Ref}
            className="opacity-0 text-white tracking-wider text-xl -translate-y-[12vh] transform-gpu [will-change:transform] font-extralight drop-shadow-[0_0_8px_rgba(239,68,68,0.85)]"
          >
            Not here to take part,
          </h1>
          <h1
            ref={sub2Ref}
            className="opacity-0 text-white tracking-wider text-xl -translate-y-[12vh] transform-gpu [will-change:transform] font-extralight drop-shadow-[0_0_8px_rgba(239,68,68,0.85)]"
          >
            But to take over.
          </h1>
          <p className="cursor-target mt-4 text-xl text-transparent [text-shadow:0_0_1px_rgba(255,255,255,0.6)]"></p>
          <div className="mt-6 flex justify-center gap-3"></div>
          <p
            ref={noteRef}
            className="opacity-0 text-white tracking-wider text-xl -translate-y-[12vh] transform-gpu [will-change:transform] font-extralight drop-shadow-[0_0_8px_rgba(239,68,68,0.85)]"
          >
            Scroll to explore what MLRITs Premiere Gaming Club has to offer.
          </p>
        </div>
      </section>
    </div>
  );
}

function Features() {
  // Prop removed
  return (
    <section
      id="features"
      className="relative min-h-screen overflow-visible px-8 sm:px-12 pb-6 sm:pb-4 pt-2"
    >
      <div className="max-w-[1100px] ml-0 mr-auto">
        <h2
          className="text-3xl cursor-target sm:text-4xl lg:text-5xl xl:text-6xl mb-6 sm:mb-8 lg:mb-10  tracking-wider uppercase text-white drop-shadow-[0_0_8px_rgba(239,68,68,0.85)]"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          About Us
        </h2>
        <p className=" text-rose-200/80 max-w-[85vw] font-light text-lg sm:max-w-[40vw] mt-8 sm:mt-12">
          We are APEX — MLRIT's premier gaming community. We host campus-wide
          tournaments, esports scrims, LAN nights, and workshops on game
          strategy, streaming, and production. Whether you’re a casual player or
          a competitive grinder, you’ll find your squad here.
        </p>

        {/* Desktop/tablet cards: centered overlay */}
        <div className="absolute inset-0 z-20 hidden sm:block">
          <CardSwap
            width={320}
            height={420}
            cardDistance={60}
            verticalDistance={70}
            delay={4500}
            pauseOnHover
            easing="elastic"
            containerClass="bottom-auto right-auto left-1/2 top-1/2 -translate-x-1/2 -translate-y-[35vh]"
          >
            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_24px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-6">
              <div className="h-full flex flex-col justify-center items-center text-center gap-3">
                <h3 className="text-2xl font-semibold text-white tracking-wide">
                  Who We Are
                </h3>
                <p className="text-rose-200/90">
                  A student-led club uniting gamers, creators, and competitors
                  at MLRIT. Built by gamers, for gamers.
                </p>
              </div>
            </Card>

            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_24px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-6">
              <div className="h-full flex flex-col justify-center items-center text-center gap-3">
                <h3 className="text-2xl font-semibold text-white tracking-wide">
                  What We Do
                </h3>
                <p className="text-rose-200/90">
                  Host Valorant, BGMI, FIFA, and multi-title events. Bootcamps,
                  casting, production, and content collabs.
                </p>
              </div>
            </Card>

            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_24px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-6">
              <div className="h-full flex flex-col justify-center items-center text-center gap-3">
                <h3 className="text-2xl font-semibold text-white tracking-wide">
                  Compete & Grow
                </h3>
                <p className="text-rose-200/90">
                  Build teams, scrim regularly, and climb leaderboards.
                  Mentorship from campus top-fraggers.
                </p>
              </div>
            </Card>

            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_24px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-6">
              <div className="h-full flex flex-col justify-center items-center text-center gap-3">
                <h3 className="text-2xl font-semibold text-white tracking-wide">
                  Community
                </h3>
                <p className="text-rose-200/90">
                  Inclusive, respectful, and hype. We celebrate wins, share
                  highlights, and make friends for life.
                </p>
              </div>
            </Card>
            {/* --- OLD REGISTRATION CARD REMOVED --- */}
          </CardSwap>
        </div>

        {/* Mobile cards: stacked below About, fit viewport, stronger left/bottom bias */}
        <div className="sm:hidden relative mt-16 z-20 flex justify-start px-2">
          <CardSwap
            width={"78vw"}
            height={430}
            cardDistance={50}
            verticalDistance={60}
            delay={4500}
            pauseOnHover
            easing="elastic"
            containerClass="relative -top-[14vh] -translate-y-0 max-[768px]:-translate-x-[35%] max-[480px]:-translate-x-[20%]"
          >
            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-5">
              <div className="h-full flex flex-col justify-center items-center text-center gap-2">
                <h3 className="text-xl font-semibold text-white tracking-wide">
                  Who We Are
                </h3>
                <p className="text-rose-200/90 text-sm">
                  A student-led club uniting gamers, creators, and competitors
                  at MLRIT.
                </p>
              </div>
            </Card>
            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-5">
              <div className="h-full flex flex-col justify-center items-center text-center gap-2">
                <h3 className="text-xl font-semibold text-white tracking-wide">
                  What We Do
                </h3>
                <p className="text-rose-200/90 text-sm">
                  Events, scrims, bootcamps, and content collabs.
                </p>
              </div>
            </Card>
            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-5">
              <div className="h-full flex flex-col justify-center items-center text-center gap-2">
                <h3 className="text-xl font-semibold text-white tracking-wide">
                  Compete & Grow
                </h3>
                <p className="text-rose-200/90 text-sm">
                  Teams, scrims, and mentorship.
                </p>
              </div>
            </Card>
            <Card customClass="bg-gradient-to-b from-[#111]/70 to-[#0b0b0c]/70 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.35),inset_0_0_0_1px_rgba(255,255,255,0.05)] p-5">
              <div className="h-full flex flex-col justify-center items-center text-center gap-2">
                <h3 className="text-xl font-semibold text-white tracking-wide">
                  Community
                </h3>
                <p className="text-rose-200/90 text-sm">
                  Hype, inclusive, respectful.
                </p>
              </div>
            </Card>
            {/* --- OLD REGISTRATION CARD REMOVED --- */}
          </CardSwap>
        </div>
      </div>
    </section>
  );
}

function Showcase() {
  // Import images directly for Vercel compatibility
  const showcaseImages = [
    new URL("../../assets/showcase/1.jpg", import.meta.url).href,
    new URL("../../assets/showcase/2.jpg", import.meta.url).href,
    new URL("../../assets/showcase/3.jpg", import.meta.url).href,
    new URL("../../assets/showcase/4.jpg", import.meta.url).href,
    new URL("../../assets/showcase/5.JPG", import.meta.url).href,
    new URL("../../assets/showcase/6.JPG", import.meta.url).href,
  ];

  return (
    <section
      id="showcase"
      className="min-h-[100vh] sm:min-h-[120vh] py-8 sm:py-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-[1100px] mx-auto">
        <h2
          className="text-3xl sm:text-4xl text-center lg:text-5xl xl:text-6xl mb-6 sm:mb-8 lg:mb-10 tracking-wider uppercase text-white drop-shadow-[0_0_8px_rgba(239,68,68,0.85)] cursor-target"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          Showcase
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
          {showcaseImages.map((image, i) => (
            <div
              key={i}
              className="relative cursor-target h-32 sm:h-40 lg:h-48 rounded-2xl overflow-hidden bg-gradient-to-b from-white/10 to-white/5 border border-crimson-500/30 shadow-[0_16px_40px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.03)] hover:shadow-[0_20px_50px_rgba(239,68,68,0.25),inset_0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-300"
            >
              <img
                src={image}
                alt={`Showcase ${i + 1}`}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="cta" className="min-h-[40vh] grid place-items-center p-5">
      <div className="text-center">
        <h2
          className="cursor-target text-4xl mb-3 font-bold tracking-wider uppercase text-white drop-shadow-[0_0_8px_rgba(239,68,68,0.85)]"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          Join APEX MLRIT
        </h2>
        <p className="cursor-target text-rose-200">
          Become part of MLRIT's premier gaming community.
        </p>
      </div>
    </section>
  );
}

function Footer({
  onMouseEnter,
  onMouseLeave,
}: {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  return (
    <footer
      className="bg-gradient-to-b from-[#0a0a0b] to-[#120709] border-t border-crimson-500/20"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 mb-8">
          {/* Brand Section */}
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start mb-4">
              <img
                src="/apexlogo.png"
                alt="APEX MLRIT"
                className="w-12 h-12 sm:w-16 sm:h-16 mr-3"
              />
              <div>
                <h3
                  className="text-xl sm:text-2xl font-bold text-white tracking-wider"
                  style={{ fontFamily: "Orbitron, sans-serif" }}
                >
                  APEX MLRIT
                </h3>
                <p className="text-rose-300/80 text-sm">Premier Gaming Club</p>
              </div>
            </div>
            <p className="text-rose-200/70 text-sm sm:text-base leading-relaxed">
              MLRIT's premier gaming community. Building champions, creating
              memories, and dominating the competition.
            </p>
          </div>

          {/* Quick Links */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold text-white mb-4 tracking-wide">
              Quick Links
            </h4>
            <ul className="space-y-2 sm:space-y-3">
              <li>
                <a
                  href="#contact"
                  className="text-rose-200/70 hover:text-crimson-400 transition-colors duration-300 text-sm sm:text-base"
                >
                  Contact
                </a>
              </li>
              <li>
                <a
                  href="#showcase"
                  className="text-rose-200/70 hover:text-crimson-400 transition-colors duration-300 text-sm sm:text-base"
                >
                  Showcase
                </a>
              </li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold text-white mb-4 tracking-wide">
              Connect With Us
            </h4>
            <div className="space-y-3 sm:space-y-4">
              <p className="text-rose-200/70 text-sm sm:text-base">
                Ready to level up? Join the APEX community today.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-3 sm:gap-4">
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/apexmlrit/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group p-2 rounded-lg bg-gradient-to-br from-crimson-500/20 to-crimson-700/20 border border-crimson-500/30 hover:border-crimson-400/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(168,85,247,0.3)]"
                >
                  <svg
                    className="w-5 h-5 text-crimson-400 hover:text-crimson-300 transition-colors duration-300"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                {/* Discord */}
                <a
                  href="https://discord.gg/TsBDQKPNe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group p-2 rounded-lg bg-gradient-to-br from-crimson-500/20 to-crimson-700/20 border border-crimson-500/30 hover:border-crimson-400/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)]"
                >
                  <svg
                    className="w-5 h-5 text-crimson-400 hover:text-crimson-300 transition-colors duration-300"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.019 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z" />
                  </svg>
                </a>
                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/apex-mlrit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group p-2 rounded-lg bg-gradient-to-br from-crimson-500/20 to-crimson-700/20 border border-crimson-500/30 hover:border-crimson-400/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(37,99,235,0.3)]"
                >
                  <svg
                    className="w-5 h-5 text-crimson-400 hover:text-crimson-300 transition-colors duration-300"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-crimson-500/20 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="text-center sm:text-left">
              <p className="text-rose-200/60 text-sm">
                © 2026 APEX MLRIT. All rights reserved.
              </p>
            </div>
            <div className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-6 text-sm"></div>
          </div>
        </div>
      </div>
    </footer>
  );
}
